import React, { useId } from 'react';
import { cn } from '@/lib/utils';
import { AlertCircle, Info } from 'lucide-react';
import Icon, { IconComponent } from './Icon';

export interface TextFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  label: string;
  leadingIcon?: IconComponent;
  /** Fixed text shown before the value, e.g. a country code. */
  prefix?: string;
  helperText?: string;
  /** Error message; replaces the helper text and turns the field red. */
  error?: string;
  /** Shown at the end of the field, e.g. a "Change" link on a locked value. */
  trailing?: React.ReactNode;
  /** Grey "filled in for you" look for read-only values, e.g. "Filled from GST records". */
  filled?: boolean;
  containerClassName?: string;
}

/** Labelled input with optional leading icon, prefix and helper/error text. */
const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  ({ label, leadingIcon, prefix, helperText, error, trailing, filled, containerClassName, className, id, ...inputProps }, ref) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const messageId = `${inputId}-message`;
    const message = error || helperText;

    return (
      <div className={cn('tw-flex tw-w-full tw-flex-col tw-gap-2', containerClassName)}>
        <label htmlFor={inputId} className="tw-text-[13px] tw-font-medium tw-leading-[18px] tw-text-ds-text-secondary">
          {label}
        </label>
        <div
          className={cn(
            'tw-flex tw-h-[52px] tw-w-full tw-items-center tw-gap-2.5 tw-rounded-xl tw-border tw-px-[14px] tw-transition-colors',
            filled ? 'tw-bg-ds-subtle' : 'tw-bg-ds-surface',
            // The design uses a 1.5px border on focus/error; the inset shadow adds the extra 0.5px without shifting layout.
            error
              ? 'tw-border-ds-danger-fg tw-shadow-[inset_0_0_0_0.5px_var(--ds-danger-fg)]'
              : inputProps.readOnly
                ? 'tw-border-ds-border-default'
                : 'tw-border-ds-border-strong focus-within:tw-border-ds-brand focus-within:tw-shadow-[inset_0_0_0_0.5px_var(--ds-brand-primary)]',
          )}
        >
          {leadingIcon && <Icon icon={leadingIcon} className="tw-text-ds-text-tertiary" />}
          {prefix && (
            <span className="tw-flex tw-shrink-0 tw-items-center tw-gap-2.5 tw-text-[15px] tw-font-medium tw-leading-5 tw-text-ds-text-primary">
              {prefix}
              <span className="tw-h-5 tw-w-px tw-bg-ds-border-strong" aria-hidden />
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            aria-invalid={error ? true : undefined}
            aria-describedby={message ? messageId : undefined}
            className={cn(
              'tw-min-w-0 tw-flex-1 tw-border-0 tw-bg-transparent tw-p-0 tw-text-[15px] tw-leading-5 tw-outline-none placeholder:tw-text-ds-text-tertiary',
              filled ? 'tw-text-ds-text-secondary' : 'tw-text-ds-text-primary',
              className,
            )}
            {...inputProps}
          />
          {trailing}
        </div>
        {message && (
          <div id={messageId} className="tw-flex tw-items-center tw-gap-1.5">
            <Icon
              icon={error ? AlertCircle : Info}
              size={14}
              className={error ? 'tw-text-ds-danger-fg' : 'tw-text-ds-text-tertiary'}
            />
            <p
              className={cn(
                'tw-flex-1 tw-text-[12px] tw-leading-4',
                error ? 'tw-font-medium tw-text-ds-danger-fg' : 'tw-text-ds-text-tertiary',
              )}
            >
              {message}
            </p>
          </div>
        )}
      </div>
    );
  },
);

TextField.displayName = 'TextField';

export default TextField;
